import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader } from '../ui/Page';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';
import { FLEET_TABS, useUrlTab } from '../ui/pageTabs';
import { WithOrgClock } from '../ui/WithOrgClock';
import { IconFleet } from '../icons';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
  worstError,
  worstState,
} from '../ui/QueryStates';
import { Link } from '@tanstack/react-router';
import { ghostButton } from '../ui/Button';
import { todayISO } from '../../lib/format';
import { FleetOverview } from './FleetOverview';
import { FleetRoutes } from './FleetRoutes';
import { FleetLeaderboard } from './FleetLeaderboard';
import type { DueRow } from './MaintenanceDueTable';

/**
 * `/fleet` (Sonar-experience 112): the shop's vans as a dashboard, and the same vans as
 * the day they are about to drive.
 *
 * ⚠️ The REGISTER is still `/fleet/assets` (107) and is not folded in as a third tab.
 * That page is a paged server table with its own three tabs; nesting it would put a
 * server pager inside a panel that must stay mounted, and it already has an address the
 * whole app links to — including this page's own "View all".
 *
 * ⭐ Wrapped in `WithOrgClock` and NOT because it prints a time as a courtesy: the date
 * this page asks about is a calendar day, the shop's zone decides which one, and the zone
 * is needed BEFORE the hooks run — it seeds `useState(todayISO(tz))` and it is an
 * argument to the day query itself. That cannot be resolved with a conditional halfway
 * down a component, which is the whole reason the wrapper exists.
 */
export function FleetPage() {
  return (
    <WithOrgClock what="your fleet" icon={IconFleet} title="Fleet">
      {(tz) => <FleetPageInner tz={tz} />}
    </WithOrgClock>
  );
}

function FleetPageInner({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const [tab, setTab] = useUrlTab(FLEET_TABS);
  const [date, setDate] = useState(() => todayISO(tz));

  const dayQuery = useQuery({
    ...trpc.fleet.day.queryOptions({ date }),
    // Hold the previous day on screen while the next one loads, so paging the date
    // does not blank the panel under the pointer that just clicked it.
    placeholderData: (prev) => prev,
  });
  const dueQuery = useQuery(trpc.asset.maintenanceDue.queryOptions());

  // `shownState`, not `queryState`: a failed BACKGROUND refresh of a board already on
  // screen is no reason to replace it with an error. The honesty belongs in
  // `refreshFailed`'s territory, not in blanking correct content (042).
  const dayState = shownState(dayQuery, dayQuery.data);
  const dueState = shownState(dueQuery, dueQuery.data);

  const due: DueRow[] = dueQuery.data ?? [];
  /*
   * ⭐ `row.card`, never a re-partition by type. 106 put `MAINTENANCE_CARD_TYPES` /
   * `REMINDER_CARD_TYPES` in shared precisely so the two cards could not be invented ad
   * hoc per surface — the register already reads this field, and a second rule here is
   * how the two screens would come to disagree about which card an inspection is on.
   */
  const maintenance = due.filter((d) => d.card === 'maintenance');
  const reminders = due.filter((d) => d.card === 'reminder');

  const day = dayQuery.data;
  // The two queries answer different questions and fail independently, so the page's
  // unresolved state is the WORSE of them — an offline day beside a loaded due list is
  // still offline.
  const overviewState = worstState(dayState, dueState);

  return (
    <PageRoot>
      <PageHeader
        title="Fleet"
        sub="Your vans, their upkeep, and the routes they'll run."
        right={
          /*
           * A real `Link`, not a `Button` with an onClick. This navigates, so it must be
           * an anchor: middle-click, ⌘-click and "open in new tab" all work on one and
           * none of them work on a button. `ghostButton` gives it the ghost variant's
           * look without adding a slot API to `Button`.
           */
          <Link to="/fleet/assets" className={ghostButton}>
            Open the register
          </Link>
        }
      />

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip
          tabs={[
            { key: 'overview', label: 'Overview' },
            // A count is a CLAIM — `counted` is the only way to make one, so the tab
            // never reads "Routes (0)" over a query that has not answered.
            {
              key: 'routes',
              label: counted('Routes', dayState, day?.vehicles.length ?? 0),
            },
          ]}
        />

        {/*
         * ⚠️ NO `keepMounted` on either panel, and that is a decision rather than an
         * omission. 105's law is that a panel whose cards hold ANY form state must stay
         * mounted or a tab click throws a draft away. Neither of these holds form state:
         * both are read-only views over queries whose data lives in the react-query
         * cache, so remounting re-reads a cache rather than re-fetching. Leaving Routes
         * unmounted is also what keeps a map out of the Overview tab.
         */}
        <UnderlineTabPanel value="overview" className="mt-4">
          {!day || !dueQuery.data ? (
            <UnresolvedState
              state={unresolved(overviewState)}
              what="your fleet"
              icon={IconFleet}
              error={worstError(dayQuery.error, dueQuery.error)}
              onRetry={() => {
                void dayQuery.refetch();
                void dueQuery.refetch();
              }}
              skeleton="h-64"
            />
          ) : (
            <>
              <StaleNotice
                show={
                  refreshFailed(dayQuery, dayQuery.data) ||
                  refreshFailed(dueQuery, dueQuery.data)
                }
                onRetry={() => {
                  void dayQuery.refetch();
                  void dueQuery.refetch();
                }}
              />
              <FleetOverview
                vehicleCount={day.vehicles.length}
                plannedMinutes={day.plannedMinutes}
                stopsWithoutDuration={day.stopsWithoutDuration}
                maintenance={maintenance}
                reminders={reminders}
                leaderboard={<FleetLeaderboard />}
              />
            </>
          )}
        </UnderlineTabPanel>

        <UnderlineTabPanel value="routes" className="mt-4">
          {!day ? (
            <UnresolvedState
              state={unresolved(dayState)}
              what="today's routes"
              icon={IconFleet}
              error={dayQuery.error}
              onRetry={() => void dayQuery.refetch()}
              skeleton="h-96"
            />
          ) : (
            <>
              <StaleNotice
                show={refreshFailed(dayQuery, dayQuery.data)}
                onRetry={() => void dayQuery.refetch()}
              />
              <FleetRoutes
                tz={tz}
                date={date}
                onDateChange={setDate}
                vehicles={day.vehicles}
                unassigned={day.unassigned}
                geocodingOn={day.geocodingOn}
                routingOn={day.routingOn}
              />
            </>
          )}
        </UnderlineTabPanel>
      </UnderlineTabs>
    </PageRoot>
  );
}
